/**
 * PlateWorkspace — the full Plate task-manager experience, shared by:
 *  - the /home/plate route (variant="page" — thin PlatePage wrapper), and
 *  - the dashboard's expand-in-place overlay (variant="overlay" — PiP-style
 *    near-full-screen panel opened from the "On Your Plate" widget).
 *
 * Owns: header + stat chips + List/Board toggle, filters, quick-add,
 * the "Tagged in" mentions section (My Plate scope), list + board views, and
 * the TaskDetailModal. View preferences persist to localStorage.
 */

import { useMemo, useState, useRef, useEffect, KeyboardEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ListTodo, Plus, AtSign, LayoutList, Columns, ArrowLeft, X } from 'lucide-react';
import { useTenantId } from '@/hooks/useTenantId';
import { useTeam } from '@/hooks/useTeam';
import { useTasks, type TaskScope } from '@/hooks/useTasks';
import { useMentions } from '@/hooks/useMentions';
import { createTodo, toggleTodo } from '@/services/todo.service';
import { extractMentions } from '@/services/mention.service';
import { notifyTaskAssignees } from '@/services/task-notify';
import { PlateFilters } from '@/components/plate/PlateFilters';
import { PlateListView, type GroupBy } from '@/components/plate/PlateListView';
import { PlateBoardView } from '@/components/plate/PlateBoardView';
import { TaskDetailModal } from '@/components/plate/TaskDetailModal';
import { TaggedInSection } from '@/components/plate/TaggedInSection';
import { Avatar } from '@/components/common/Avatar';
import type { TodoItem, TodoAssignee, TaskStatus, TaskPriority, TaskEntityKind } from '@/types/schema';
import type { MentionableUser } from '@/types/mention.types';
import {
  applyMention,
  findMentionTrigger,
  matchesMentionQuery,
  mentionHandleFor,
} from '@/utils/mention-trigger';

function filterBucket(arr: TodoItem[], match: (t: TodoItem) => boolean): TodoItem[] {
  return arr.filter(match);
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// View preferences persist per browser so the workspace reopens exactly how
// you left it (view / group-by / scope / show-done) — page and overlay share.
const PREFS_KEY = 'plate_view_prefs';

interface PlatePrefs {
  scope?: TaskScope;
  groupBy?: GroupBy;
  view?: 'list' | 'board';
  showDone?: boolean;
}

function readPrefs(): PlatePrefs {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
  } catch {
    return {};
  }
}

// ============================================================================
// PlateOverlay — PiP-style near-full-screen expansion of the widget
// ============================================================================

export function PlateOverlay({ onClose }: { onClose: () => void }) {
  // Lock body scroll while expanded.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-[150] bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="fixed inset-2 sm:inset-6 lg:inset-10 rounded-3xl border border-theme bg-surface-page shadow-2xl overflow-y-auto overscroll-contain"
      >
        <PlateWorkspace variant="overlay" onClose={onClose} />
      </motion.div>
    </motion.div>
  );
}

// ============================================================================
// PlateWorkspace
// ============================================================================

interface PlateWorkspaceProps {
  variant: 'page' | 'overlay';
  /** Overlay only — close the expanded panel. */
  onClose?: () => void;
}

export function PlateWorkspace({ variant, onClose }: PlateWorkspaceProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = useTenantId();
  const userId = localStorage.getItem('user_id') || '';
  const userName = localStorage.getItem('logged_in_user') || '';
  const userRole = localStorage.getItem('user_role') || 'Office';
  const { team } = useTeam();
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);

  const prefs = useRef(readPrefs()).current;
  const [scope, setScope] = useState<TaskScope>(prefs.scope === 'team' ? 'team' : 'mine');
  const [personId, setPersonId] = useState('');
  const [groupBy, setGroupBy] = useState<GroupBy>(
    prefs.groupBy && ['status', 'priority', 'assignee', 'entity'].includes(prefs.groupBy)
      ? prefs.groupBy
      : 'status',
  );
  const [view, setView] = useState<'list' | 'board'>(prefs.view === 'board' ? 'board' : 'list');
  const [showDone, setShowDone] = useState(!!prefs.showDone);
  const [search, setSearch] = useState('');

  // Persist view preferences.
  useEffect(() => {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ scope, groupBy, view, showDone }));
  }, [scope, groupBy, view, showDone]);

  const { all, tasks, byStatus, byPriority, byEntity, byAssignee, loading } = useTasks({
    scope,
    userId,
    tenantId,
    personId: personId || undefined,
  });

  // "Tagged in" — the stuff you're @mentioned in. These are mentions/, NOT
  // tasks, so without this section the full Plate looked empty ("clean
  // plate") while the widget showed a pile of tags.
  const { mentions, unreadCount: unreadMentionCount, openMention, clearMention, clearReadMentions } =
    useMentions({ userId });

  // Deep-link (page variant only): /home/plate?taskId=... opens the task
  // detail modal (mentions + reminders link here). Runs on EVERY searchParams
  // change — not just mount — so clicking a mention while already on the
  // Plate page still opens the task. Param stripped after consumption.
  useEffect(() => {
    if (variant !== 'page') return;
    const linked = searchParams.get('taskId');
    if (!linked) return;
    setOpenTaskId(linked);
    const next = new URLSearchParams(searchParams);
    next.delete('taskId');
    next.delete('noteId');
    setSearchParams(next, { replace: true });
  }, [variant, searchParams, setSearchParams]);

  const openTask = useMemo(() => all.find((t) => t.id === openTaskId) || null, [all, openTaskId]);
  const childTasks = useMemo(
    () => (openTaskId ? all.filter((t) => t.parentTaskId === openTaskId) : []),
    [all, openTaskId],
  );

  // Overlay: Escape closes the panel — but NOT while a task modal is open
  // (the modal has its own Esc listener; without this guard one keypress
  // would close both layers). Guards on openTask (the RENDERED modal), not
  // openTaskId: if the open task is deleted remotely, the modal unmounts
  // without resetting openTaskId, and a stale-id guard would leave Esc
  // permanently dead. Same-keypress protection is preserved — this listener
  // reads the pre-flush closure value when the modal's own Esc fires.
  useEffect(() => {
    if (variant !== 'overlay' || !onClose) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && !openTask) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [variant, onClose, openTask]);

  const availableUsers: MentionableUser[] = useMemo(
    () =>
      team.map((m) => ({
        id: m.id,
        name: m.name,
        firstName: m.name.split(' ')[0],
        lastName: m.name.split(' ').slice(1).join(' '),
        role: m.dualAccess ? 'Office' : m.role,
        isActive: m.active,
      })),
    [team],
  );

  // Apply search across the pre-grouped buckets.
  const q = search.trim().toLowerCase();
  const match = useMemo(
    () => (t: TodoItem) =>
      !q ||
      t.title.toLowerCase().includes(q) ||
      (t.sourceLabel || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q),
    [q],
  );

  const filtered = useMemo(() => {
    const fStatus = {} as Record<TaskStatus, TodoItem[]>;
    (Object.keys(byStatus) as TaskStatus[]).forEach((k) => (fStatus[k] = filterBucket(byStatus[k], match)));
    const fPriority = {} as Record<TaskPriority, TodoItem[]>;
    (Object.keys(byPriority) as TaskPriority[]).forEach((k) => (fPriority[k] = filterBucket(byPriority[k], match)));
    const fEntity = {} as Record<TaskEntityKind, TodoItem[]>;
    (Object.keys(byEntity) as TaskEntityKind[]).forEach((k) => (fEntity[k] = filterBucket(byEntity[k], match)));
    const fAssignee = byAssignee
      .map((g) => ({ ...g, tasks: filterBucket(g.tasks, match) }))
      .filter((g) => g.tasks.length > 0);
    return { fStatus, fPriority, fEntity, fAssignee };
  }, [byStatus, byPriority, byEntity, byAssignee, match]);

  const stats = useMemo(() => {
    const today = todayStr();
    const weekAgo = new Date(Date.now() - 7 * 86_400_000).toISOString();
    let open = 0;
    let overdue = 0;
    let dueToday = 0;
    let doneThisWeek = 0;
    tasks.forEach((t) => {
      if (t.status === 'done') {
        if ((t.completedAt || '') >= weekAgo) doneThisWeek += 1;
        return;
      }
      open += 1;
      if (t.dueDate && t.dueDate < today) overdue += 1;
      else if (t.dueDate === today) dueToday += 1;
    });
    return { open, overdue, dueToday, doneThisWeek };
  }, [tasks]);

  // Shown in BOTH scopes — mentions are the viewer's own attention items, and
  // scope persists from prefs: a user whose last view was Team would otherwise
  // expand into a "clean plate" while tagged in a pile of things.
  const showTagged = mentions.length > 0;

  const handleToggleComplete = (task: TodoItem, done: boolean) => {
    if (task.id) toggleTodo(task.id, done, userId).catch(() => {});
  };

  const handleOpen = (task: TodoItem) => {
    if (task.id) setOpenTaskId(task.id);
  };

  /** Route change from inside the workspace — closes the overlay first so the
   *  destination page isn't hidden behind the expanded panel. */
  const handleNavigate = (path: string) => {
    setOpenTaskId(null);
    onClose?.();
    navigate(path);
  };

  return (
    <div
      className={`${view === 'board' ? 'max-w-[1400px]' : 'max-w-5xl'} mx-auto px-4 py-6 space-y-5 transition-all`}
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {variant === 'page' && (
            <button
              type="button"
              onClick={() => navigate('/home')}
              className="w-9 h-9 rounded-xl bg-surface-raised text-theme-muted hover:text-theme-primary ring-1 ring-theme-soft flex items-center justify-center transition-colors shrink-0"
              aria-label="Back to dashboard"
              title="Back to dashboard"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div className="w-10 h-10 rounded-2xl bg-brand-secondary/15 text-brand-secondary flex items-center justify-center">
            <ListTodo className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-theme-primary leading-tight">Plate</h1>
            <p className="text-xs text-theme-muted">
              {scope === 'mine' ? 'Everything on your plate' : 'The whole team’s plate'}
            </p>
          </div>
          {/* Glanceable stat chips — wrap to their own row on small screens
              instead of disappearing (overdue counts matter most on a phone) */}
          {!loading && (
            <div className="flex items-center flex-wrap gap-1.5 w-full sm:w-auto sm:ml-3">
              <StatChip label="Open" value={stats.open} className="bg-surface-raised text-theme-secondary" />
              {/* Unread only — a tag you've already opened isn't outstanding. */}
              {unreadMentionCount > 0 && (
                <StatChip label="Tagged in" value={unreadMentionCount} className="bg-blue-500/15 text-blue-400" />
              )}
              {stats.dueToday > 0 && (
                <StatChip label="Due today" value={stats.dueToday} className="bg-amber-500/15 text-amber-400" />
              )}
              {stats.overdue > 0 && (
                <StatChip label="Overdue" value={stats.overdue} className="bg-red-500/15 text-red-400" pulse />
              )}
              {stats.doneThisWeek > 0 && (
                <StatChip label="Done this wk" value={stats.doneThisWeek} className="bg-emerald-500/15 text-emerald-400" />
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* List / Board toggle */}
          <div className="flex gap-1 bg-surface-raised rounded-xl p-1">
            {([
              { key: 'list' as const, label: 'List', Icon: LayoutList },
              { key: 'board' as const, label: 'Board', Icon: Columns },
            ]).map(({ key, label, Icon }) => {
              const active = view === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setView(key)}
                  className={`px-3 py-2.5 sm:py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                    active
                      ? 'bg-brand-secondary text-brand-primary shadow-md'
                      : 'text-theme-muted hover:text-theme-secondary'
                  }`}
                  aria-pressed={active}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              );
            })}
          </div>

          {variant === 'overlay' && (
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl bg-surface-raised text-theme-muted hover:text-theme-primary ring-1 ring-theme-soft flex items-center justify-center transition-colors shrink-0"
              aria-label="Close"
              title="Close (Esc)"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <PlateFilters
        scope={scope}
        setScope={setScope}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        personId={personId}
        setPersonId={setPersonId}
        showDone={showDone}
        setShowDone={setShowDone}
        hideShowDone={view === 'board'}
        search={search}
        setSearch={setSearch}
        team={team.map((m) => ({ id: m.id, name: m.name }))}
      />

      <QuickAddTask
        userId={userId}
        userName={userName}
        userRole={userRole}
        tenantId={tenantId}
        availableUsers={availableUsers}
        team={team}
        existingCount={tasks.length}
      />

      {/* Tagged in — the viewer's own mentions, visible in every scope */}
      {showTagged && (
        <TaggedInSection
          mentions={mentions}
          onClear={(id) => clearMention(id).catch(() => {})}
          onNavigate={handleNavigate}
          onOpen={(mention) => openMention(mention).catch(() => {})}
          onClearRead={() => clearReadMentions().catch(() => {})}
        />
      )}

      {loading ? (
        <PlateSkeleton board={view === 'board'} />
      ) : view === 'board' ? (
        <PlateBoardView
          groupBy={groupBy}
          byStatus={filtered.fStatus}
          byPriority={filtered.fPriority}
          byEntity={filtered.fEntity}
          byAssignee={filtered.fAssignee}
          actor={{ id: userId, name: userName }}
          onToggleComplete={handleToggleComplete}
          onOpen={handleOpen}
        />
      ) : (
        <PlateListView
          groupBy={groupBy}
          byStatus={filtered.fStatus}
          byPriority={filtered.fPriority}
          byEntity={filtered.fEntity}
          byAssignee={filtered.fAssignee}
          showDone={showDone}
          hasTaggedItems={showTagged}
          onToggleComplete={handleToggleComplete}
          onOpen={handleOpen}
        />
      )}

      {openTask && (
        <TaskDetailModal
          /* Keyed by task id so switching tasks (subtask click, deep-link)
             remounts the modal — resets edit drafts, delete-confirm, and the
             subtask-count sync ref instead of leaking them across tasks. */
          key={openTask.id}
          task={openTask}
          childTasks={childTasks}
          availableUsers={availableUsers}
          team={team.map((m) => ({ id: m.id, name: m.name, role: m.role, dualAccess: m.dualAccess }))}
          actor={{ id: userId, name: userName, role: userRole }}
          tenantId={tenantId}
          onClose={() => setOpenTaskId(null)}
          onOpenTask={(id) => setOpenTaskId(id)}
          onNavigate={handleNavigate}
        />
      )}
    </div>
  );
}

// ============================================================================
// StatChip + skeleton
// ============================================================================

function StatChip({
  label,
  value,
  className,
  pulse = false,
}: {
  label: string;
  value: number;
  className: string;
  pulse?: boolean;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${className}`}
    >
      {pulse && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
      <span className="font-bold">{value}</span>
      {label}
    </span>
  );
}

function PlateSkeleton({ board }: { board: boolean }) {
  if (board) {
    return (
      <div className="flex gap-3 overflow-hidden animate-pulse">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex-shrink-0 w-[280px] rounded-2xl border border-theme bg-surface-raised/40 p-2 space-y-2" style={{ opacity: 1 - i * 0.15 }}>
            <div className="h-8 bg-surface-hover/60 rounded-xl" />
            {[0, 1].map((j) => (
              <div key={j} className="h-20 bg-surface-hover/40 rounded-xl" />
            ))}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-2 animate-pulse">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="rounded-xl border border-theme bg-surface-card p-3 pl-4 flex items-start gap-2.5"
          style={{ opacity: 1 - i * 0.12 }}
        >
          <div className="w-[18px] h-[18px] rounded-full bg-surface-hover shrink-0" />
          <div className="w-7 h-7 rounded-lg bg-surface-hover shrink-0" />
          <div className="flex-1 space-y-2 pt-0.5">
            <div className="h-3 bg-surface-hover rounded w-2/3" />
            <div className="h-2.5 bg-surface-hover/60 rounded w-1/3" />
          </div>
          <div className="w-6 h-6 rounded-full bg-surface-hover shrink-0" />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// QuickAddTask — inline title input with @mention co-assign
// ============================================================================

function QuickAddTask({
  userId,
  userName,
  userRole,
  tenantId,
  availableUsers,
  team,
  existingCount,
}: {
  userId: string;
  userName: string;
  userRole: string;
  tenantId: string;
  availableUsers: MentionableUser[];
  team: { id: string; name: string; role: string; dualAccess?: boolean }[];
  existingCount: number;
}) {
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<MentionableUser[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? title.length;
    // Shared trigger rules (src/utils/mention-trigger.ts): an "@" opens the
    // picker whenever it starts a word — mid-sentence and after punctuation
    // both work, while an email address in the text stays quiet.
    const trigger = findMentionTrigger(title, cursor);
    if (!trigger) {
      setShowSuggestions(false);
      return;
    }
    const filtered = availableUsers.filter((u) => matchesMentionQuery(u, trigger.query));
    if (filtered.length > 0) {
      setSuggestions(filtered);
      setShowSuggestions(true);
      setSelectedIdx(0);
    } else {
      setShowSuggestions(false);
    }
  }, [title, availableUsers]);

  const insertMention = (user: MentionableUser) => {
    const el = inputRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? title.length;
    const trigger = findMentionTrigger(title, cursor);
    if (!trigger) return;

    const next = applyMention(
      title,
      cursor,
      trigger.atIndex,
      mentionHandleFor(user, availableUsers),
    );
    setTitle(next.text);
    setShowSuggestions(false);
    setTimeout(() => {
      el.setSelectionRange(next.cursor, next.cursor);
      el.focus();
    }, 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx((p) => (p + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx((p) => (p - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && suggestions.length > 0)) {
        e.preventDefault();
        insertMention(suggestions[selectedIdx]);
        return;
      }
      if (e.key === 'Escape') {
        // Esc here means "dismiss the dropdown" ONLY — stop it reaching the
        // overlay's window Esc listener, which would close the whole panel
        // and destroy the half-typed draft.
        e.stopPropagation();
        setShowSuggestions(false);
        return;
      }
    }
    if (e.key === 'Enter' && !showSuggestions) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const trimmed = title.trim();
    if (!trimmed || submitting || !userId) return;
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      const mentions = extractMentions(trimmed, availableUsers);
      const others = mentions.filter((m) => m.userId !== userId);

      const assignees: TodoAssignee[] = [
        { userId, userName, role: userRole, addedAt: now },
      ];
      others.forEach((m) => {
        if (assignees.some((a) => a.userId === m.userId)) return;
        const member = team.find((t) => t.id === m.userId);
        const role = member?.dualAccess ? 'Office' : member?.role || 'Office';
        assignees.push({ userId: m.userId, userName: m.userName, role, addedAt: now });
      });

      const taskId = await createTodo({
        tenantId,
        title: trimmed,
        completed: false,
        status: 'todo',
        priority: 'normal',
        entityKind: 'general',
        order: existingCount,
        createdBy: userId,
        createdByName: userName,
        assignees,
      });

      if (others.length > 0) {
        notifyTaskAssignees({
          taskId,
          title: trimmed,
          assigneeUserIds: assignees.map((a) => a.userId),
          actor: { id: userId, name: userName },
          tenantId,
          availableUsers,
        }).catch(() => {});
      }

      setTitle('');
      inputRef.current?.focus();
    } catch {
      // keep input on failure so the user can retry
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {/* Opens DOWNWARD — this input sits near the top of the workspace (unlike
          the dashboard's bottom-anchored quick-add, which opens upward). */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-2 rounded-xl border border-theme bg-surface-card backdrop-blur-xl shadow-xl overflow-hidden z-20">
          {suggestions.slice(0, 6).map((user, i) => (
            <button
              key={user.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(user);
              }}
              onMouseEnter={() => setSelectedIdx(i)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                i === selectedIdx ? 'bg-brand-secondary/15' : 'hover:bg-surface-hover'
              }`}
            >
              <Avatar name={user.name} userId={user.id} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-theme-primary truncate">{user.name}</p>
                <p className="text-[10px] text-theme-muted">{user.role}</p>
              </div>
              <AtSign className="w-3 h-3 text-theme-muted/50" />
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 rounded-xl bg-surface-card border border-theme focus-within:ring-2 focus-within:ring-brand-secondary/40 transition-all px-3 py-2">
        <button
          type="button"
          onClick={() => inputRef.current?.focus()}
          className="shrink-0 w-7 h-7 rounded-lg bg-brand-secondary/15 flex items-center justify-center text-brand-secondary"
          aria-label="Add a task"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
        </button>
        <input
          ref={inputRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a task to your plate… use @ to assign"
          disabled={submitting}
          className="flex-1 min-w-0 bg-transparent text-sm text-theme-primary placeholder:text-theme-muted focus:outline-none disabled:opacity-50"
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!title.trim() || submitting}
          className={`shrink-0 text-xs font-bold px-3 py-2 sm:py-1.5 rounded-lg transition-all ${
            title.trim() && !submitting
              ? 'bg-brand-secondary text-brand-primary hover:brightness-110 active:scale-95'
              : 'bg-surface-hover text-theme-muted/50 cursor-not-allowed'
          }`}
        >
          Add
        </button>
      </div>
    </div>
  );
}
